<template>
  <div class="task-list" :class="{ 'wxwork-mobile': isWxWorkMobile }">
    <div class="header">
      <h2>工作任务列表</h2>
      <div class="task-tab">
        <button :class="{ active: tabType === 'TODO' }" @click="switchTab('TODO')">我的待办</button>
        <button :class="{ active: tabType === 'APPROVAL' }" @click="switchTab('APPROVAL')">我的待审批</button>
      </div>
    </div>

    <!-- 移动端：卡片式任务列表 -->
    <div class="mobile-task-list" v-if="isWxWorkMobile">
      <div class="mobile-scroll-wrapper">
        <div class="mobile-task-card" v-for="task in displayTasks" :key="task.id" :class="getTaskStatusClass(task.status)">
          <!-- 任务头部：ID + 标题 + 分数 -->
          <div class="task-header">
            <div class="task-id-score">
              <span class="task-id">#{{ task.id }}</span>
              <span class="task-score" v-if="task.score">分值：{{ task.score }}</span>
            </div>
            <h3 class="task-title">{{ task.title }}</h3>
          </div>
          
          <!-- 任务核心信息 -->
          <div class="task-info-grid">
            <div class="task-item">
              <span class="label">创建人：</span>
              <span class="value">{{ task.creatorUserId || '无' }}</span>
            </div>
            <div class="task-item">
              <span class="label">完成人：</span>
              <span class="value">{{ task.assigneeUserId || '无' }}</span>
            </div>
            <div class="task-item">
              <span class="label">审核节点数：</span>
              <span class="value">{{ task.auditLevelCount || 1 }}级</span>
            </div>
            <div class="task-item">
              <span class="label">当前审核级：</span>
              <span class="value">{{ task.currentAuditLevel || 1 }}级</span>
            </div>
            <div class="task-item">
              <span class="label">截止时间：</span>
              <span class="value">{{ formatDate(task.dueDate) }}</span>
            </div>
            <div class="task-item">
              <span class="label">创建时间：</span>
              <span class="value">{{ formatDate(task.createdAt) }}</span>
            </div>
          </div>
          
          <!-- 新增：文件上传/查看模块【移动端核心修改】 -->
          <div class="task-file-section">
            <div class="desc-toggle" @click="toggleFileDesc(task.id)">
              <span>附件文件 {{ showFileDesc[task.id] ? '收起' : '展开' }}</span>
              <i class="arrow-icon">{{ showFileDesc[task.id] ? '↑' : '↓' }}</i>
            </div>
            <div class="file-content" v-if="showFileDesc[task.id]">
              <!-- 完成人可上传文件（提交后禁用） -->
              <div class="file-upload-wrap" v-if="tabType === 'TODO' && canOperateTask(task) && !isCompleteDescLocked(task)">
                  <!-- 关键：为每个文件选择框添加唯一ID，避免冲突 -->
                  <input :id="`task-file-upload-${task.id}`" type="file" class="file-input" @change="handleFileUpload(task, $event)" multiple />
                  <!-- 直接通过ID触发点击，放弃ref方式 -->
                  <button 
                    class="upload-btn" 
                    @click="handleUploadBtnClick(`task-file-upload-${task.id}`)"
                  >
                    选择文件上传
                  </button>
                  <span class="upload-tip">支持多文件上传，提交后不可再上传</span>
                </div>
              
              <!-- 所有角色可查看文件 -->
              <div class="file-list-wrap" v-if="task.files && task.files.length > 0">
                <div class="file-list-title">已上传文件：</div>
                <div class="file-item" v-for="(file, index) in task.files" :key="index">
                  <i class="file-icon">📄</i>
                  <span class="file-name">{{ file.fileName }}</span>
                  <span class="file-size">({{ formatFileSize(file.fileSize) }})</span>
                  <button class="view-file-btn" @click="viewFile(file)">查看</button>
                </div>
              </div>
              <div class="no-file-tip" v-else>暂无上传文件</div>
            </div>
          </div>
          
          <!-- 任务完成情况：提交后不可编辑 -->
          <div class="task-complete-desc">
            <div class="desc-toggle" @click="toggleCompleteDesc(task.id)">
              <span>完成情况 {{ showCompleteDesc[task.id] ? '收起' : '展开' }}</span>
              <i class="arrow-icon">{{ showCompleteDesc[task.id] ? '↑' : '↓' }}</i>
            </div>
            <div class="desc-content" v-if="showCompleteDesc[task.id]">
              <textarea
                v-if="canEditCompleteDesc(task) && !isCompleteDescLocked(task)"
                v-model="task.completeDescription"
                class="edit-textarea"
                placeholder="请输入任务完成情况描述..."
                rows="3"
              ></textarea>
              <div v-else class="view-text">{{ task.completeDescription || '无完成情况' }}</div>
              <button
                v-if="canEditCompleteDesc(task) && !isCompleteDescLocked(task)"
                @click="saveCompleteDesc(task)"
                class="save-btn small"
              >
                保存完成情况
              </button>
            </div>
          </div>
          
          <!-- 多级审核人信息：审核操作后不可编辑 -->
          <div class="task-auditors">
            <div class="desc-toggle" @click="toggleAuditorDesc(task.id)">
              <span>审核人信息 {{ showAuditorDesc[task.id] ? '收起' : '展开' }}</span>
              <i class="arrow-icon">{{ showAuditorDesc[task.id] ? '↑' : '↓' }}</i>
            </div>
            <div class="auditor-content" v-if="showAuditorDesc[task.id]">
              <div class="auditor-title">审核人信息：</div>
              <div class="auditor-list">
                <div class="auditor-item" v-for="(auditor, index) in task.auditors" :key="auditor.id || index">
                  <span class="auditor-level">第{{ auditor.auditLevel }}级：</span>
                  <span class="auditor-name">{{ auditor.auditUserId }}</span>
                  <span class="auditor-status" :class="`status-${auditor.auditStatus.toLowerCase()}`">
                    {{ formatAuditStatus(auditor.auditStatus) }}
                  </span>
                  <div class="audit-opinion-wrap">
                    <textarea
                      v-if="canEditAuditOpinion(task, auditor) && !isAuditOpinionLocked(auditor)"
                      v-model="auditor.auditOpinion"
                      class="edit-textarea mini"
                      placeholder="请输入审核意见..."
                      rows="2"
                    ></textarea>
                    <span
                      v-else
                      class="auditor-opinion"
                      @click="showAuditOpinion(auditor)"
                    >
                      {{ auditor.auditOpinion ? '查看意见' : '无意见' }}
                    </span>
                  </div>
                  <button
                    v-if="canEditAuditOpinion(task, auditor) && !isAuditOpinionLocked(auditor)"
                    @click="saveAuditOpinion(task, auditor)"
                    class="save-btn tiny"
                  >
                    保存意见
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 状态操作：按钮组 + 防重复禁用逻辑 -->
          <div class="task-status-action">
            <span class="label">当前状态：</span>
            <span class="current-status" :class="getTaskStatusClass(task.status)">{{ formatStatusShow(task.status) }}</span>
            <div class="status-btn-group" v-if="tabType === 'TODO' && canOperateTask(task)">
              <button 
                class="status-btn submit-btn" 
                @click="updateTaskStatus(task, 'PENDING_APPROVAL')"
                :disabled="!canSubmitTask(task)"
              >
                提交
              </button>
            </div>
            <div class="status-btn-group" v-else-if="tabType === 'APPROVAL' && canOperateTask(task)">
              <button 
                class="status-btn approve-btn" 
                @click="updateTaskStatus(task, 'APPROVED')"
                :disabled="!canAuditTask(task)"
              >
                审核通过
              </button>
              <button 
                class="status-btn reject-btn" 
                @click="updateTaskStatus(task, 'REJECTED')"
                :disabled="!canAuditTask(task)"
              >
                驳回
              </button>
            </div>
            <div class="status-btn-group disabled" v-else>无操作权限</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 桌面端：表格布局【桌面端核心修改】 -->
    <table v-else>
      <thead>
        <tr>
          <th>任务ID</th>
          <th>标题</th>
          <th>分值</th>
          <th>创建人</th>
          <th>完成人</th>
          <th>审核节点数</th>
          <th>当前审核级</th>
          <th>完成情况</th>
          <th>附件文件</th> <!-- 新增：文件列 -->
          <th>当前状态</th>
          <th>截止时间</th>
          <th>审核人/意见</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="task in displayTasks" :key="task.id">
          <td>{{ task.id }}</td>
          <td>{{ task.title }}</td>
          <td>{{ task.score || 0 }}</td>
          <td>{{ task.creatorUserId || '无' }}</td>
          <td>{{ task.assigneeUserId || '无' }}</td>
          <td>{{ task.auditLevelCount || 1 }}</td>
          <td>{{ task.currentAuditLevel || 1 }}</td>
          <td class="complete-desc-cell">
            <span class="desc-toggle desktop" @click="toggleCompleteDesc(task.id)">
              {{ showCompleteDesc[task.id] ? '收起' : '查看/编辑' }}
            </span>
            <div class="desc-content desktop" v-if="showCompleteDesc[task.id]">
              <textarea
                v-if="canEditCompleteDesc(task) && !isCompleteDescLocked(task)"
                v-model="task.completeDescription"
                class="edit-textarea"
                placeholder="请输入任务完成情况描述..."
                rows="2"
              ></textarea>
              <div v-else class="view-text">{{ task.completeDescription || '无完成情况' }}</div>
              <button
                v-if="canEditCompleteDesc(task) && !isCompleteDescLocked(task)"
                @click="saveCompleteDesc(task)"
                class="save-btn small"
              >
                保存
              </button>
            </div>
          </td>
          <!-- 新增：文件列（桌面端） -->
          <td class="file-cell">
            <span class="desc-toggle desktop" @click="toggleFileDesc(task.id)">
              {{ showFileDesc[task.id] ? '收起文件' : '查看/上传文件' }}
            </span>
            <div class="file-content desktop" v-if="showFileDesc[task.id]">
              <!-- 完成人可上传文件 -->
              <div class="file-upload-wrap desktop" v-if="tabType === 'TODO' && canOperateTask(task) && !isCompleteDescLocked(task)">
                <input
                  type="file"
                  ref="fileInputDesktop"
                  @change="handleFileUpload(task, $event)"
                  class="file-input"
                  multiple
                />
                <button class="upload-btn small" @click="triggerFileInputDesktop">上传文件</button>
              </div>
              <!-- 所有角色可查看文件 -->
              <div class="file-list-wrap desktop" v-if="task.files && task.files.length > 0">
                <div class="file-item desktop" v-for="(file, index) in task.files" :key="index">
                  <i class="file-icon">📄</i>
                  <span class="file-name">{{ file.fileName }}</span>
                  <span class="file-size">({{ formatFileSize(file.size) }})</span>
                  <button class="view-file-btn tiny" @click="viewFile(file)">查看</button>
                </div>
              </div>
              <div class="no-file-tip" v-else>暂无文件</div>
            </div>
          </td>
          <td><span class="current-status" :class="getTaskStatusClass(task.status)">{{ formatStatusShow(task.status) }}</span></td>
          <td>{{ formatDate(task.dueDate) }}</td>
          <td class="auditor-cell">
            <span class="desc-toggle desktop" @click="toggleAuditorDesc(task.id)">
              {{ showAuditorDesc[task.id] ? '收起审核人' : '查看审核人' }}
            </span>
            <div v-if="showAuditorDesc[task.id]">
              <div v-if="task.auditors && task.auditors.length">
                <div v-for="(auditor, index) in task.auditors" :key="auditor.id || index">
                  <div class="auditor-info">
                    第{{ auditor.auditLevel }}级：{{ auditor.auditUserId }} 
                    ({{ formatAuditStatus(auditor.auditStatus) }})
                  </div>
                  <div class="audit-opinion-edit">
                    <textarea
                      v-if="canEditAuditOpinion(task, auditor) && !isAuditOpinionLocked(auditor)"
                      v-model="auditor.auditOpinion"
                      class="edit-textarea mini"
                      placeholder="审核意见..."
                      rows="1"
                    ></textarea>
                    <span
                      v-else
                      @click="showAuditOpinion(auditor)"
                      class="view-opinion"
                    >
                      {{ auditor.auditOpinion || '无意见' }}
                    </span>
                  </div>
                  <button
                    v-if="canEditAuditOpinion(task, auditor) && !isAuditOpinionLocked(auditor)"
                    @click="saveAuditOpinion(task, auditor)"
                    class="save-btn tiny"
                  >
                    保存意见
                  </button>
                </div>
              </div>
              <span v-else>无审核人</span>
            </div>
          </td>
          <td>
            <div class="status-btn-group table-btn-group" v-if="tabType === 'TODO' && canOperateTask(task)">
              <button 
                class="status-btn submit-btn" 
                @click="updateTaskStatus(task, 'PENDING_APPROVAL')"
                :disabled="!canSubmitTask(task)"
              >
                提交
              </button>
            </div>
            <div class="status-btn-group table-btn-group" v-else-if="tabType === 'APPROVAL' && canOperateTask(task)">
              <button 
                class="status-btn approve-btn" 
                @click="updateTaskStatus(task, 'APPROVED')"
                :disabled="!canAuditTask(task)"
              >
                审核通过
              </button>
              <button 
                class="status-btn reject-btn" 
                @click="updateTaskStatus(task, 'REJECTED')"
                :disabled="!canAuditTask(task)"
              >
                驳回
              </button>
            </div>
            <span class="no-permission" v-else>无操作权限</span>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="displayTasks.length === 0" class="empty">
      {{ tabType === 'TODO' ? '暂无待办任务' : '暂无待审批任务' }}
    </div>
    <div v-if="loading" class="loading">加载中...</div>

    <!-- 审核意见弹窗 -->
    <div class="modal-mask" v-if="showAuditModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>第{{ currentAuditor.auditLevel }}级审核意见</h3>
          <button class="close-btn" @click="showAuditModal = false">×</button>
        </div>
        <div class="modal-body">
          <p class="auditor-name">审核人：{{ currentAuditor.auditUserId }}</p>
          <p class="audit-time" v-if="currentAuditor.auditTime">
            审核时间：{{ formatDate(currentAuditor.auditTime) }}
          </p>
          <div class="audit-opinion-content">
            {{ currentAuditor.auditOpinion || '无审核意见' }}
          </div>
        </div>
        <div class="modal-footer">
          <button class="confirm-btn" @click="showAuditModal = false">确定</button>
        </div>
      </div>
    </div>

    <!-- 新增：文件查看弹窗 -->
    <div class="modal-mask" v-if="showFileModal">
      <div class="modal-content file-modal">
        <div class="modal-header">
          <h3>查看文件：{{ currentFile?.name }}</h3>
          <button class="close-btn" @click="showFileModal = false">×</button>
        </div>
        <div class="modal-body file-modal-body">
          <!-- 根据文件类型展示不同内容 -->
          <div v-if="isTextFile(currentFile)">
            <pre class="text-file-content">{{ currentFileContent }}</pre>
          </div>
          <div v-else-if="isImageFile(currentFile)">
            <img :src="require(currentFile.filePath)" alt="文件预览" class="image-file-content" />
          </div>
          <div v-else>
            <div class="other-file-tip">
              <i class="file-icon-big">📄</i>
              <p>不支持预览该类型文件，可下载查看</p>
              <button class="download-file-btn" @click="downloadFile(currentFile)">下载文件</button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="download-file-btn" @click="downloadFile(currentFile)">下载文件</button>
          <button class="confirm-btn" @click="showFileModal = false">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getUserTasks, getUserApprovalTasks, updateTaskStatus, saveCompleteDesc, saveAuditOpinion, uploadTaskFile, getTaskFile } from '@/api/task';
import { isWxWorkEnv } from '@/utils/request';

export default {
  name: 'TaskList',
  data() {
    return {
      tasks: [],
      approvalTasks: [],
      currentUserId: '',
      isWxWorkMobile: false,
      retryCount: 0,
      tabType: 'TODO',
      loading: false,
      showCompleteDesc: {},
      showAuditorDesc: {},
      showFileDesc: {}, // 新增：文件展开状态
      showAuditModal: false,
      showFileModal: false, // 新增：文件弹窗状态
      currentAuditor: {},
      currentFile: {}, // 新增：当前查看的文件
      currentFileContent: '' // 新增：文本文件内容
    };
  },
  computed: {
    displayTasks() {
      return this.tabType === 'TODO' ? this.tasks : this.approvalTasks;
    }
  },
  created() {
    this.isWxWorkMobile = isWxWorkEnv() && /mobile/i.test(navigator.userAgent);
    this.initCurrentUser();
  },
  mounted() {
    const checkUser = () => {
      if (this.currentUserId) {
        this.fetchTasks();
      } else {
        this.retryCount < 3 ? (this.retryCount++, setTimeout(checkUser, 200)) : (this.$message?.error('未检测到登录态，请重新登录'), this.$router.push('/login'));
      }
    };
    this.retryCount = 0;
    checkUser();
  },
  methods: {
    switchTab(type) {
      this.tabType = type;
      this.showCompleteDesc = {};
      this.showAuditorDesc = {};
      this.showFileDesc = {}; // 新增：切换标签重置文件展开状态
    },
    handleUploadBtnClick(inputId) {
      // 解决移动端/企业微信兼容性问题：延迟触发 + 强制聚焦
      setTimeout(() => {
        const fileInput = document.getElementById(inputId);
        if (fileInput) {
          // 重置选择框（避免重复选择同一文件无反应）
          fileInput.value = '';
          // 触发点击（兼容所有浏览器）
          fileInput.click();
        } else {
          console.error('文件选择框未找到：', inputId);
          this.$message?.error('文件上传组件加载失败，请刷新重试');
        }
      }, 100);
    },
    getTaskStatusClass(status) {
      const map = { 
        PENDING: 'status-pending', 
        IN_PROGRESS: 'status-progress', 
        PENDING_APPROVAL: 'status-pending-approval', 
        APPROVED: 'status-approved', 
        REJECTED: 'status-rejected' 
      };
      return map[status] || '';
    },
    canOperateTask(task) {
      if (this.tabType === 'TODO') return task.assigneeUserId === this.currentUserId;
      if (!task.auditors || task.auditors.length === 0) return false;
      const currentAuditor = task.auditors.find(a => a.auditStatus === 'PENDING' && a.auditLevel === task.currentAuditLevel);
      return currentAuditor && currentAuditor.auditUserId === this.currentUserId;
    },
    canSubmitTask(task) {
      const allowStatus = ['PENDING', 'IN_PROGRESS', 'REJECTED'];
      return allowStatus.includes(task.status);
    },
    canAuditTask(task) {
      const currentAuditor = task.auditors?.find(a => a.auditStatus === 'PENDING' && a.auditLevel === task.currentAuditLevel);
      return task.status === 'PENDING_APPROVAL' && !!currentAuditor;
    },
    isCompleteDescLocked(task) {
      const lockStatus = ['PENDING_APPROVAL', 'APPROVED', 'REJECTED'];
      return lockStatus.includes(task.status);
    },
    isAuditOpinionLocked(auditor) {
      const lockStatus = ['APPROVED', 'REJECTED'];
      return lockStatus.includes(auditor.auditStatus);
    },
    canEditCompleteDesc(task) {
      return task.assigneeUserId === this.currentUserId;
    },
    canEditAuditOpinion(task, auditor) {
      if (!auditor || !auditor.auditUserId || !task.currentAuditLevel) return false;
      return auditor.auditUserId === this.currentUserId && auditor.auditLevel === task.currentAuditLevel;
    },
    async saveCompleteDesc(task) {
      if (!task.completeDescription || task.completeDescription.trim() === '') {
        return this.$message?.warning('完成情况不能为空，请输入内容！');
      }
      if (this.isCompleteDescLocked(task)) {
        return this.$message?.warning('任务已提交，完成情况不可编辑！');
      }
      try {
        const res = await saveCompleteDesc( task.id,task.completeDescription.trim());
        if (res && res.success && res.code === 200) {
          this.$message?.success('任务完成情况保存成功！');
          this.fetchTasks();
        } else {
          this.$message?.error(res?.msg || '完成情况保存失败！');
        }
      } catch (error) {
        console.error('保存完成情况异常：', error);
        this.$message?.error('网络异常，保存失败！');
      }
    },
    async saveAuditOpinion(task, auditor) {
      if (this.isAuditOpinionLocked(auditor)) {
        return this.$message?.warning('该审核节点已操作，意见不可编辑！');
      }
      try {
        const auditOpinion = auditor.auditOpinion ? auditor.auditOpinion.trim() : '';
        const res = await saveAuditOpinion(task.id,auditor.auditLevel, auditOpinion);
        if (res && res.success && res.code === 200) {
          this.$message?.success('审核意见保存成功！');
          this.fetchTasks();
        } else {
          this.$message?.error(res?.msg || '审核意见保存失败！');
        }
      } catch (error) {
        console.error('保存审核意见异常：', error);
        this.$message?.error('网络异常，保存失败！');
      }
    },
    toggleCompleteDesc(taskId) {
      this.$set(this.showCompleteDesc, taskId, !this.showCompleteDesc[taskId]);
    },
    toggleAuditorDesc(taskId) {
      this.$set(this.showAuditorDesc, taskId, !this.showAuditorDesc[taskId]);
    },
    // 新增：文件展开/收起
    toggleFileDesc(taskId) {
      this.$set(this.showFileDesc, taskId, !this.showFileDesc[taskId]);
    },
    showAuditOpinion(auditor) {
      this.currentAuditor = auditor;
      this.showAuditModal = true;
    },
    formatAuditStatus(status) {
      const map = { PENDING: '待审核', APPROVED: '已通过', REJECTED: '已驳回' };
      return map[status] || '未知状态';
    },
    formatStatusShow(status) {
      const map = { 
        PENDING: '待开始', 
        IN_PROGRESS: '进行中', 
        PENDING_APPROVAL: '已完成', 
        APPROVED: '审核通过', 
        REJECTED: '已驳回' 
      };
      return map[status] || '未知状态';
    },
    initCurrentUser() {
      try {
        setTimeout(() => {
          this.currentUserId = localStorage.getItem('adminName') || '';
          if (!this.currentUserId) {
            const token = localStorage.getItem('accessToken');
            if (token) {
              const payload = JSON.parse(decodeURIComponent(escape(atob(token.split('.')[1]))));
              this.currentUserId = payload.sub || '';
              localStorage.setItem('adminName', this.currentUserId);
            }
          }
        }, 100);
      } catch (e) {
        console.error('读取当前用户ID失败：', e);
        this.currentUserId = '';
      }
    },
    async fetchTasks() {
      if (!this.currentUserId) return this.$message?.warning('未检测到有效登录用户，无法获取任务列表');
      this.loading = true;
      try {
        const [todoRes, approvalRes] = await Promise.all([getUserTasks(this.currentUserId), getUserApprovalTasks(this.currentUserId)]);
        this.tasks = this.formatTaskData(todoRes);
        this.approvalTasks = this.formatTaskData(approvalRes).map(task => ({ ...task, auditLevelCount: task.auditLevelCount || 1, currentAuditLevel: task.currentAuditLevel || 1, auditors: task.auditors || [] }));
        this.tabType === 'TODO' && this.tasks.length > 0 && this.$message?.success(`成功加载 ${this.tasks.length} 个待办任务`);
        this.tabType === 'APPROVAL' && this.approvalTasks.length > 0 && this.$message?.success(`成功加载 ${this.approvalTasks.length} 个待审批任务`);
      } catch (error) {
        console.error('获取任务列表异常：', error);
        this.$message?.error('网络异常或后端服务未启动，无法获取任务列表');
        this.tasks = [];
        this.approvalTasks = [];
      } finally {
        this.loading = false;
      }
    },
    formatTaskData(res) {
      if (!res || !res.success || res.code !== 200) return [];
      const rawTasks = res.data || [];
      return Array.isArray(rawTasks) ? rawTasks.filter(task => task && task.id).map(task => ({
        ...task,
        creatorUserId: task.creatorUserId || '未知',
        assigneeUserId: task.assigneeUserId || '未知',
        score: task.score || 0,
        auditLevelCount: task.auditLevelCount || 1,
        currentAuditLevel: task.currentAuditLevel || 1,
        completeDescription: task.completeDescription || '',
        auditors: task.auditors || [{ id: `default-${task.id}`, auditLevel: 1, auditUserId: '暂无审核人', auditOpinion: '', auditStatus: 'PENDING', auditTime: null }],
        status: task.status || 'PENDING',
        files: task.files || [] // 新增：初始化文件列表
      })) : [];
    },
    async updateTaskStatus(task, targetStatus) {
      if (!task.id) return this.$message?.warning('任务ID异常，无法更新状态');
      if (targetStatus === 'PENDING_APPROVAL' && !this.canSubmitTask(task)) {
        return this.$message?.warning('当前状态无法提交，仅驳回后可重新提交！');
      }
      if ([ 'APPROVED', 'REJECTED' ].includes(targetStatus) && !this.canAuditTask(task)) {
        return this.$message?.warning('当前状态无法审核，仅待审核任务可操作！');
      }
      try {
        const res = await updateTaskStatus(task.id, targetStatus);
        if (res && res.success && res.code === 200) {
          this.$message?.success(res.msg || `任务${this.getStatusText(targetStatus)}成功！`);
          this.fetchTasks();
        } else {
          this.$message?.error(res?.msg || `任务${this.getStatusText(targetStatus)}失败！`);
          this.fetchTasks();
        }
      } catch (error) {
        console.error('更新任务状态异常：', error);
        this.$message?.error('网络异常，无法更新任务状态');
        this.fetchTasks();
      }
    },
    getStatusText(status) {
      const map = { PENDING_APPROVAL: '提交', APPROVED: '审核通过', REJECTED: '驳回' };
      return map[status] || '状态更新';
    },
    formatDate(dateStr) {
      if (!dateStr) return '无';
      try {
        const date = new Date(dateStr);
        return this.isWxWorkMobile ? `${date.toLocaleDateString('zh-CN')} ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}` : date.toLocaleString('zh-CN');
      } catch (error) {
        console.error('日期格式化失败：', error);
        return dateStr;
      }
    },
    // ===== 新增：文件相关核心方法 =====
    // 触发文件选择框
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    triggerFileInputDesktop() {
      this.$refs.fileInputDesktop.click();
    },
    // 处理文件上传
   async handleFileUpload(task, event) {
  const files = event.target.files;
  if (!files || files.length === 0) return;

  // 构建FormData（严格按后端要求）
  const formData = new FormData();
  formData.append('taskId', task.id); // 任务ID
  // 多文件循环添加
  for (let i = 0; i < files.length; i++) {
    formData.append('files', files[i]); // 后端接收文件的key需为files
  }

  try {
    this.loading = true;
    // 调用上传接口
    const res = await uploadTaskFile(formData);
    if (res && res.success && res.code === 200) {
      this.$message?.success(`成功上传 ${files.length} 个文件！`);
      // 刷新任务列表（确保文件列表同步）
      this.fetchTasks();
    } else {
      this.$message?.error(res?.msg || '文件上传失败，请重试');
    }
  } catch (error) {
    console.error('文件上传异常：', error);
    this.$message?.error('网络异常或后端服务未启动，文件上传失败');
  } finally {
    this.loading = false;
    // 重置选择框
    event.target.value = '';
  }
},

    // 查看文件
    async viewFile(file) {
      this.currentFile = file;
      this.showFileModal = true;
      
      // 加载文件内容（仅文本/图片类型）
      if (this.isTextFile(file) || this.isImageFile(file)) {
        try {
          const res = await getTaskFile(file.id);
          if (res && res.success) {
            if (this.isTextFile(file)) {
              this.currentFileContent = res.data;
            } else if (this.isImageFile(file)) {
              // 图片文件直接使用URL
              this.currentFileContent = file.filePath;
            }
          }
        } catch (error) {
          console.error('加载文件内容失败：', error);
          this.$message?.warning('无法预览文件内容，可下载查看');
        }
      }
    },
    // 下载文件
    downloadFile(file) {
      const link = document.createElement('a');
      link.href = file.filePath;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    // 格式化文件大小
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    // 判断是否为文本文件
    isTextFile(file) {
      if (!file || !file.fileName) return false;
      const textExtensions = ['.txt', '.json', '.md', '.js', '.css', '.html', '.xml'];
      return textExtensions.some(ext => file.fileName.toLowerCase().endsWith(ext));
    },
    // 判断是否为图片文件
    isImageFile(file) {
      if (!file || !file.fileName) return false;
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
      return imageExtensions.some(ext => file.fileName.toLowerCase().endsWith(ext));
    }
  }
};
</script>

<style scoped>
/* 基础样式保留，新增【文件相关样式】核心修改 */
.task-list {
  max-width: 1200px;
  margin: 20px auto;
  padding: 20px;
  border: 1px solid #eee;
  border-radius: 8px;
  box-sizing: border-box;
}
.task-tab { display: flex; gap: 8px; }
.task-tab button { padding: 6px 16px; border: 1px solid #dcdfe6; border-radius: 4px; background: #fff; cursor: pointer; font-size: 14px; color: #606266; transition: all 0.2s; }
.task-tab button.active { background: #409eff; color: #fff; border-color: #409eff; }
.wxwork-mobile { max-width: 100% !important; margin: 0 !important; padding: 10px !important; border: none !important; min-height: 100vh; background-color: #f5f7fa; overflow: hidden; }
.wxwork-mobile .header { margin-bottom: 15px; flex-direction: column; align-items: flex-start; gap: 10px; }
.wxwork-mobile .header h2 { font-size: 20px; text-align: left; margin: 0; color: #1d2129; font-weight: 600; }
.mobile-scroll-wrapper { width: 100%; max-height: calc(100vh - 120px); overflow-y: auto; -webkit-overflow-scrolling: touch; padding-bottom: 20px; }
.mobile-task-card { padding: 18px; border-radius: 12px; margin-bottom: 12px; background-color: #fff; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06); transition: all 0.2s ease; border: 0; }
.status-pending { color: #86909c; }
.status-progress { color: #409eff; }
.status-pending-approval { color: #f7ba1e; }
.status-approved { color: #52c41a; }
.status-rejected { color: #f5222d; }
.mobile-task-card.status-pending { border-left: 4px solid #86909c; }
.mobile-task-card.status-progress { border-left: 4px solid #409eff; }
.mobile-task-card.status-pending-approval { border-left: 4px solid #f7ba1e; }
.mobile-task-card.status-approved { border-left: 4px solid #52c41a; }
.mobile-task-card.status-rejected { border-left: 4px solid #f5222d; }
.mobile-task-card:hover { box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); transform: translateY(-2px); }
.task-header { margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #f0f2f5; }
.task-id-score { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.task-id { font-size: 13px; color: #86909c; font-weight: 500; }
.task-score { font-size: 13px; color: #fa8c16; background-color: #fff7e6; padding: 2px 8px; border-radius: 10px; }
.task-title { font-size: 16px; color: #1d2129; margin: 0; font-weight: 600; }
.task-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
.task-item { display: flex; flex-direction: column; margin: 0; line-height: 1.4; }
.task-item .label { font-size: 13px; color: #86909c; margin-bottom: 2px; }
.task-item .value { font-size: 14px; color: #4e5969; font-weight: 500; }
.task-complete-desc { margin: 10px 0; }
.desc-toggle { display: flex; justify-content: space-between; align-items: center; font-size: 13px; color: #409eff; cursor: pointer; padding: 4px 0; }
.arrow-icon { font-size: 12px; color: #86909c; }
.desc-content { font-size: 14px; color: #4e5969; line-height: 1.6; padding: 8px 0; white-space: pre-wrap; }
.auditor-content {
  font-size: 14px;
  color: #4e5969;
  line-height: 1.6;
  padding: 8px 0;
  white-space: pre-wrap;
}
.task-auditors { margin: 10px 0; padding: 10px; background-color: #f9fafc; border-radius: 8px; }
.auditor-title { font-size: 13px; color: #86909c; margin-bottom: 8px; }
.auditor-list { display: flex; flex-direction: column; gap: 8px; }
.auditor-item { display: flex; align-items: flex-start; flex-wrap: wrap; gap: 8px; font-size: 14px; padding: 4px 0; border-bottom: 1px dashed #eee; }
.auditor-item:last-child { border-bottom: none; }
.auditor-level { color: #86909c; }
.auditor-name { color: #4e5969; min-width: 60px; }
.auditor-status { padding: 2px 6px; border-radius: 4px; font-size: 12px; }
.auditor-status.status-pending { background-color: #f5f5f5; color: #86909c; }
.auditor-status.status-approved { background-color: #f0f9ff; color: #52c41a; }
.auditor-status.status-rejected { background-color: #fff1f0; color: #f5222d; }

/* 状态按钮组样式 + 禁用样式 */
.task-status-action {
  display: flex;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #f0f2f5;
  gap: 12px;
  flex-wrap: wrap;
}
.task-status-action .label { font-size: 14px; color: #86909c; }
.current-status {
  font-size: 14px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
}
.status-btn-group {
  display: flex;
  gap: 8px;
  margin-left: auto;
}
.table-btn-group { margin-left: 0; justify-content: center; }
.status-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  color: #fff;
}
.submit-btn { background-color: #409eff; }
.submit-btn:hover { background-color: #337ecc; }
.approve-btn { background-color: #52c41a; }
.approve-btn:hover { background-color: #41a80f; }
.reject-btn { background-color: #f5222d; }
.reject-btn:hover { background-color: #d31522; }
.status-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
.status-btn-group.disabled, .no-permission {
  font-size: 12px;
  color: #909399;
  margin-left: auto;
}
.no-permission { text-align: center; }

/* 锁定态样式 */
.view-text {
  width: 100%;
  padding: 8px;
  line-height: 1.6;
  color: #4e5969;
  background-color: #f9fafc;
  border-radius: 4px;
}
.auditor-opinion {
  font-size: 12px;
  color: #409eff;
  cursor: pointer;
  text-decoration: underline;
}
.view-opinion {
  font-size: 13px;
  color: #409eff;
  cursor: pointer;
}

.empty { text-align: center; padding: 40px 20px; color: #86909c; font-size: 14px; background-color: #fff; border-radius: 12px; margin: 10px 0; }
.loading { text-align: center; padding: 40px 20px; color: #409eff; font-size: 14px; }
.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
table { width: 100%; border-collapse: collapse; }
th, td { border: 1px solid #ccc; padding: 10px; text-align: left; }
th { background-color: #f5f5f5; }
.desc-toggle.desktop { color: #409eff; cursor: pointer; font-size: 13px; }
.desc-content.desktop { font-size: 13px; color: #4e5969; padding: 4px 0; white-space: pre-wrap; }
.modal-mask { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-content { width: 90%; max-width: 500px; background-color: #fff; border-radius: 8px; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15); }
.modal-header { padding: 16px; border-bottom: 1px solid #f0f2f5; display: flex; justify-content: space-between; align-items: center; }
.modal-header h3 { margin: 0; font-size: 16px; color: #1d2129; }
.close-btn { background: none; border: none; font-size: 20px; color: #86909c; cursor: pointer; padding: 0 8px; }
.modal-body { padding: 16px; }
.auditor-name { font-size: 14px; color: #4e5969; margin: 0 0 8px 0; }
.audit-time { font-size: 13px; color: #86909c; margin: 0 0 12px 0; }
.audit-opinion-content { font-size: 14px; color: #4e5969; line-height: 1.6; white-space: pre-wrap; min-height: 80px; padding: 10px; background-color: #f9fafc; border-radius: 4px; }
.modal-footer { padding: 12px 16px; border-top: 1px solid #f0f2f5; text-align: right; }
.confirm-btn { padding: 6px 16px; background-color: #409eff; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; }
.mobile-scroll-wrapper::-webkit-scrollbar { width: 4px; }
.mobile-scroll-wrapper::-webkit-scrollbar-track { background: #f0f2f5; border-radius: 2px; }
.mobile-scroll-wrapper::-webkit-scrollbar-thumb { background: #dcdfe6; border-radius: 2px; }
.mobile-scroll-wrapper::-webkit-scrollbar-thumb:hover { background: #c0c4cc; }
.edit-textarea {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  resize: none;
  font-size: 14px;
  color: #4e5969;
  box-sizing: border-box;
}
.edit-textarea.mini {
  width: calc(100% - 80px);
  min-width: 150px;
}
.save-btn {
  margin-top: 8px;
  padding: 6px 16px;
  background-color: #409eff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}
.save-btn:hover {
  background-color: #337ecc;
}
.save-btn.small {
  padding: 4px 12px;
  font-size: 13px;
}
.save-btn.tiny {
  padding: 2px 8px;
  font-size: 12px;
  margin-top: 0;
}
.audit-opinion-wrap {
  flex: 1;
  min-width: 120px;
}
.complete-desc-cell { min-width: 150px; }
.auditor-cell { min-width: 200px; }
.auditor-info { margin-bottom: 4px; font-size: 13px; }
.audit-opinion-edit { margin-bottom: 4px; }

/* ===== 核心新增：文件相关样式 ===== */
/* 文件模块容器 */
.task-file-section {
  margin: 10px 0;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 8px;
}
.file-content {
  padding: 8px 0;
}
/* 文件上传样式 */
.file-input {
  display: none;
}
.upload-btn {
  padding: 6px 16px;
  background-color: #1890ff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  margin-bottom: 8px;
}
.upload-btn:hover {
  background-color: #096dd9;
}
.upload-btn.small {
  padding: 4px 12px;
  font-size: 12px;
}
.upload-tip {
  font-size: 12px;
  color: #86909c;
}
/* 文件列表样式 */
.file-list-wrap {
  margin-top: 8px;
}
.file-list-title {
  font-size: 13px;
  color: #86909c;
  margin-bottom: 6px;
}
.file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px dashed #eee;
  font-size: 13px;
}
.file-item:last-child {
  border-bottom: none;
}
.file-item.desktop {
  flex-wrap: wrap;
}
.file-icon {
  font-size: 16px;
}
.file-name {
  flex: 1;
  color: #4e5969;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-size {
  font-size: 12px;
  color: #86909c;
}
/* 查看文件按钮 */
.view-file-btn {
  padding: 2px 8px;
  background-color: #409eff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}
.view-file-btn.tiny {
  padding: 1px 6px;
  font-size: 11px;
}
.view-file-btn:hover {
  background-color: #337ecc;
}
/* 无文件提示 */
.no-file-tip {
  font-size: 13px;
  color: #86909c;
  padding: 8px 0;
  text-align: center;
}
/* 文件列样式 */
.file-cell {
  min-width: 180px;
}
/* 文件弹窗样式 */
.file-modal {
  max-width: 80%;
  max-height: 80vh;
  overflow: hidden;
}
.file-modal-body {
  max-height: 60vh;
  overflow-y: auto;
}
/* 文本文件内容 */
.text-file-content {
  width: 100%;
  padding: 10px;
  background-color: #f9fafc;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  max-height: 50vh;
  overflow-y: auto;
}
/* 图片文件内容 */
.image-file-content {
  max-width: 100%;
  max-height: 50vh;
  object-fit: contain;
  border-radius: 4px;
}
/* 其他文件提示 */
.other-file-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
}
.file-icon-big {
  font-size: 48px;
  margin-bottom: 16px;
  color: #86909c;
}
/* 下载按钮 */
.download-file-btn {
  padding: 6px 16px;
  background-color: #52c41a;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  margin-right: 8px;
}
.download-file-btn:hover {
  background-color: #41a80f;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .task-list { max-width: 100%; margin: 10px; padding: 10px; }
  table { font-size: 14px; }
  th, td { padding: 8px 5px; }
  .status-btn { padding: 4px 8px; font-size: 12px; }
  .task-status-action { gap: 8px; }
  .edit-textarea.mini { width: calc(100% - 70px); }
  .file-modal {
    max-width: 95%;
  }
}
</style>